import { LineageDag, LineageEdge, LineageNode } from "types/lineage.types"

/**
 * Combines several lineage DAGs into one.
 *
 * A merged data file inherits its derived variables from the scripts that ran on each
 * side BEFORE the merge, so its lineage is the union of the per-side DAGs captured at
 * merge time. The same script usually runs on both waves, so most edges are identical
 * and collapse; where a wave's data made a definition resolve differently, both edges
 * survive and the difference is visible.
 *
 * Nodes are unioned by id, and a node counts as `derived` if it was derived in ANY
 * input — a variable built on one wave is a derived variable of the merged file.
 * Edges are deduped on target + command + defHash + sources: `defHash` fingerprints the
 * exact definition, so identical definitions collapse and genuinely different ones do not.
 *
 * @param dags - The DAGs to combine (empty / missing ones are ignored)
 * @returns One DAG; `{nodes: [], edges: []}` when there is nothing to combine
 */
export const unionLineageDags = (dags: (LineageDag | null | undefined)[]): LineageDag => {
  const nodes = new Map<string, LineageNode>()
  const edges = new Map<string, LineageEdge>()
  let schema: number | undefined

  for (const dag of dags) {
    if (!dag) continue
    if (dag.schema != null) schema = Math.max(schema ?? 0, dag.schema)

    for (const n of dag.nodes ?? []) {
      const existing = nodes.get(n.id)
      // Derived anywhere => derived here. Otherwise a leaf on one side would mask the
      // fact that the other side built it.
      if (!existing) nodes.set(n.id, { ...n })
      else if (n.derived && !existing.derived) existing.derived = true
    }

    for (const e of dag.edges ?? []) {
      const key = [e.target, e.command, e.defHash, (e.sources ?? []).join("")].join("")
      if (!edges.has(key)) edges.set(key, { ...e })
    }
  }

  return {
    ...(schema != null ? { schema } : {}),
    nodes: [...nodes.values()],
    edges: [...edges.values()],
  }
}
